import { AssetGroup } from '@play-co/timestep-core';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { Card, CardState } from '../components/Card';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { downscale } from 'src/conf/scale';

type PopupCardTradeShareInviteClose = (accepted?: boolean) => void;

export type PopupCardTradeShareInviteOpenArgs = {
  cardState: CardState;
};

const POPUP_WIDTH = 614;
const POPUP_HEIGHT = 845;
const CONTENT_CENTER_X = POPUP_WIDTH / 2;
const TITLE_BANNER_Y = 20;
const CARD_SCALE = 1.8;
const CARD_Y = 330;
const MESSAGE_Y = 625;
const BUTTON_Y = POPUP_HEIGHT - 105;
const SEND_CONTAINER_WIDTH = 430;
const ACTION_BUTTON_WIDTH = 200;
const ACTION_BUTTON_HEIGHT = 82;
const DETAILS_BINDING_Y = [190, 700];

export default class PopupCardTradeShareInvite extends PopupBasic<PopupCardTradeShareInviteClose> {
  private card: Card;

  constructor(opts: {
    superview: View;
    close: PopupCardTradeShareInviteClose;
  }) {
    super({
      superview: opts.superview,
      boxOpts: {
        image: 'assets/ui/cards/common/cardbook_frame.png',
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: { left: 80, center: 80, right: 80 },
          vertical: { top: 80, middle: 80, bottom: 80 },
        },
      },
      skipTitle: true,
      width: POPUP_WIDTH,
      height: POPUP_HEIGHT,
      close: () => opts.close(false),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.TRADE,
      },
      zIndex: 10000,
    });

    this.buttonClose.removeFromSuperview();

    const titleBanner = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/cards/common/banner_title.png',
      width: downscale(576),
      height: downscale(132),
      x: CONTENT_CENTER_X,
      y: TITLE_BANNER_Y,
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });

    new LangBitmapFontTextShadowView({
      superview: titleBanner,
      width: titleBanner.style.width - 90,
      height: 58,
      x: titleBanner.style.width / 2,
      y: titleBanner.style.height / 2 - 10,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 36,
      font: 'Title',
      color: '#FFF5B5',
      textShadowColor: '#8D2E21',
      textShadowOffsetX: 0,
      textShadowOffsetY: 4,
      canHandleEvents: false,
      localeText: () => i18n('cards.cardTrade.shareInviteTitle'),
    });

    for (const y of DETAILS_BINDING_Y) {
      new ImageScaleView({
        superview: this.box,
        image: 'assets/ui/cards/common/details_binding.png',
        width: 50,
        height: 79,
        x: 12,
        y,
        centerOnOrigin: true,
        scaleMethod: 'contain',
      });
    }

    this.card = new Card({
      superview: this.box,
      centerOnOrigin: true,
      x: CONTENT_CENTER_X,
      y: CARD_Y,
      scale: CARD_SCALE,
    });

    this.message.updateOpts({
      width: 500,
      height: 130,
      x: CONTENT_CENTER_X,
      y: MESSAGE_Y,
      font: 'Body',
      size: 32,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      color: '#9F5D58',
    });

    const buttonContainer = new View({
      superview: this.box,
      width: SEND_CONTAINER_WIDTH,
      height: ACTION_BUTTON_HEIGHT,
      x: CONTENT_CENTER_X,
      y: BUTTON_Y,
      centerOnOrigin: true,
    });

    new ButtonScaleViewWithText({
      superview: buttonContainer,
      ...buttons.cancel,
      width: ACTION_BUTTON_WIDTH,
      height: ACTION_BUTTON_HEIGHT,
      x: 10,
      fontSize: 32,
      font: 'Body',
      labelOffsetX: -2,
      localeText: () => i18n('cards.cardTrade.reject'),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.CANCEL,
      },
      onClick: async () => {
        sounds.sfx.play('cancel');
        opts.close(false);
      },
    });

    new ButtonScaleViewWithText({
      superview: buttonContainer,
      ...buttons.secondary,
      width: ACTION_BUTTON_WIDTH,
      height: ACTION_BUTTON_HEIGHT,
      x: 220,
      fontSize: 32,
      font: 'Body',
      labelOffsetX: -2,
      localeText: () => i18n('cards.cardTrade.accept'),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.TRADE,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        opts.close(true);
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup(AssetGroup.constructURLs(['assets/ui/cards/common']));
  }

  public init({ cardState }: PopupCardTradeShareInviteOpenArgs) {
    super.init({
      message: i18n('cards.cardTrade.shareInviteMessage'),
    });
    this.card.setState(cardState);
  }
}
